import React, { useState } from 'react';
import {
  SxProps,
  Typography,
  TypographyPropsVariantOverrides,
} from '@mui/material';
import CustomTextField from 'shared/lib/components/controls/textFields/customTextField';
import { emptySelectItemId } from 'shared/lib/utils/globalUtils';
import map from 'lodash-es/map';
import { SelectItem } from 'shared/types/selectItem';
import { useTranslation } from 'react-i18next';
import CommonMenuItem from 'shared/lib/components/menu/commonMenuItem';
import type { Variant } from '@mui/material/styles/createTypography';

export type LabelProps = {
  sx?: SxProps;
  typographyVariantSize?: keyof TypographyPropsVariantOverrides | Variant;
  text?: string;
  textSection?: string;
  textFolder?: string;
};

export type CustomSelectProps<T extends string> = {
  value: T;
  onChange: (val: T) => void;
  items: SelectItem<T>[];
  label?: string;
  isEmptyItem?: boolean;
  sx?: SxProps;
  wrapperSxProps?: SxProps;
  disabled?: boolean;
  error?: boolean;
  renderItems?: (items: SelectItem<T>[]) => React.ReactNode;
  labelProps?: LabelProps;
};

export default function CustomSelect<T extends string>({
  value,
  label,
  isEmptyItem = true,
  items,
  sx,
  wrapperSxProps,
  labelProps,
  onChange,
  disabled,
  error,
  renderItems,
}: CustomSelectProps<T>) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const roundedBorders: SxProps = open
    ? {
        fieldset: {
          borderBottom: '0 !important',
          borderBottomLeftRadius: '0px !important',
          borderBottomRightRadius: '0px !important',
        },
      }
    : {};

  return (
    <CustomTextField
      select
      error={error}
      value={value || emptySelectItemId}
      onChange={(e) => onChange(e.target.value as T)}
      wrapperSxProps={{ width: 1, ...wrapperSxProps }}
      label={label}
      labelTypographyVariantSize={labelProps?.typographyVariantSize}
      labelSxProps={labelProps?.sx}
      sx={{
        ...roundedBorders,
        '& .MuiInputBase-root': {
          pr: 0,
        },
        '& .MuiInputBase-input': {
          display: 'flex',
          alignItems: 'center',
          color: value ? 'text.primary' : 'text.secondary',
          // @ts-ignore
          ...sx?.['& .MuiInputBase-input'],
        },
        ...sx,
      }}
      disabled={disabled}
      SelectProps={{
        open,
        onOpen: (e) => {
          setOpen(true);
        },
        onClose: (e) => {
          setOpen(false);
        },
        MenuProps: {
          slotProps: {
            paper: {
              sx: {
                border: 1,
                borderColor: 'custom.borders',
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              },
            },
          },
        },
      }}
    >
      {renderItems
        ? renderItems(items)
        : [
            isEmptyItem && (
              <CommonMenuItem
                sx={{ color: 'text.secondary' }}
                value={emptySelectItemId}
              >
                <Typography variant="f16">{t('common.notSelected')}</Typography>
              </CommonMenuItem>
            ),

            ...map(items, (item) => (
              <CommonMenuItem key={item.id} value={item.id}>
                <Typography variant="f16">{item.name}</Typography>
              </CommonMenuItem>
            )),
          ]}
    </CustomTextField>
  );
}
